import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { MdChevronRight } from "react-icons/md";

import { useHomeCoordinatorProjects } from "../hooks/useHomeTracking";
import type {
  HomeLoadCoordinator,
  HomeLoadRow,
  HomeScope,
  HomeTracking,
} from "../types/home.types";
import {
  daysSince,
  formatShortDay,
  saoPauloDayKey,
} from "../utils/homeFilters";
import { NoApprovalChip, StatusChip } from "./HomeChip";
import { HomePanel } from "./HomePanel";
import { ALERT_TONE, HOME_COLORS } from "./homeTheme";

/**
 * Coluna "Devolvidos" oculta a pedido do QA: o status Devolvido ainda não
 * existe no projeto, então ela sempre mostraria 0. A Api continua mandando o
 * número; para reabilitar, basta voltar para true.
 */
const SHOW_RETURNED_COLUMN = false;

const STAT_COLUMNS = SHOW_RETURNED_COLUMN ? 5 : 4;
const GRID = `20px minmax(170px, 1.8fr) repeat(${STAT_COLUMNS}, minmax(64px, 1fr))`;

const ellipsis = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

interface LoadPanelProps {
  scope: HomeScope;
  today?: string;
  load: NonNullable<HomeTracking["load"]>;
  onOpenProject(projectId: string): void;
  /** Lista de Projetos do coordenador (e da célula, vindo da visão por célula). */
  onOpenCoordinator(coordinator: HomeLoadCoordinator, cell?: HomeLoadRow): void;
  onOpenCell(cell: HomeLoadRow): void;
}

/**
 * Carga por coordenador (Minha célula) ou visão por célula (Todos). Ordem
 * fixa por projetos ativos; uma linha expandida por vez.
 */
export function LoadPanel({
  scope,
  today,
  load,
  onOpenProject,
  onOpenCoordinator,
  onOpenCell,
}: LoadPanelProps) {
  const [openRow, setOpenRow] = useState<string | null>(null);
  const byCell = load.kind === "celula";
  const max = Math.max(1, ...load.rows.map((row) => row.stats.ativos));
  const title = byCell ? "Visão por célula" : "Carga por coordenador";
  const summary = byCell
    ? `${load.rows.length} células, ${load.activeTotal} projetos ativos`
    : `${load.rows.length} coordenadores, ${load.activeTotal} projetos ativos` +
      (load.countsSecondCoordinator
        ? " (projetos com 2 coordenadores contam na linha de cada um)"
        : "");

  return (
    <HomePanel
      title={title}
      aside="Ordenado por projetos ativos"
      flex="2 1 560px"
    >
      <Typography sx={{ fontSize: 12, color: HOME_COLORS.muted, mt: -1 }}>
        {summary}
      </Typography>
      <Box sx={{ overflowX: "auto" }}>
        <Box sx={{ minWidth: 600 }}>
          <Box
            display="grid"
            gridTemplateColumns={GRID}
            gap={1}
            sx={{
              px: 0.5,
              py: 1,
              fontSize: 12,
              fontWeight: 500,
              color: HOME_COLORS.muted,
            }}
          >
            <span />
            <span>{byCell ? "Célula" : "Coordenador"}</span>
            <span>Ativos</span>
            <Box textAlign="center">Próx. 7 dias</Box>
            <Box textAlign="center">Aprov. parada</Box>
            {SHOW_RETURNED_COLUMN && <Box textAlign="center">Devolvidos</Box>}
            <Box textAlign="center">Alertas</Box>
          </Box>
          <Box sx={{ maxHeight: 560, overflowY: "auto" }}>
            {load.rows.length === 0 && (
              <Typography
                sx={{ py: 2, color: HOME_COLORS.muted, fontSize: 13 }}
              >
                {byCell
                  ? "Nenhuma célula cadastrada."
                  : "Nenhum coordenador na sua célula."}
              </Typography>
            )}
            {load.rows.map((row) => {
              const open = openRow === row.id;
              return (
                <Box key={row.id}>
                  <LoadRow
                    row={row}
                    subtitle={
                      byCell
                        ? `Líder: ${row.leaders?.join(", ") || "—"}`
                        : "Coord. Produção"
                    }
                    max={max}
                    open={open}
                    onToggle={() => setOpenRow(open ? null : row.id)}
                  />
                  {open && (
                    <Box
                      display="flex"
                      flexDirection="column"
                      gap={0.5}
                      // Na célula, os coordenadores usam as colunas da
                      // tabela; no coordenador, a prévia fica recuada.
                      sx={{ pl: byCell ? 0 : 3.25, pb: 1.75 }}
                    >
                      {byCell ? (
                        <CellCoordinators
                          cell={row}
                          onOpenCoordinator={(coordinator) =>
                            onOpenCoordinator(coordinator, row)
                          }
                          onOpenCell={() => onOpenCell(row)}
                        />
                      ) : (
                        <CoordinatorPreview
                          scope={scope}
                          today={today}
                          coordinator={row}
                          onOpenProject={onOpenProject}
                          onOpenList={() => onOpenCoordinator(row)}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </HomePanel>
  );
}

function Count({ value }: { value: number }) {
  return (
    <Box
      textAlign="center"
      sx={{
        fontSize: 14,
        fontWeight: value > 0 ? 500 : 400,
        color: value > 0 ? HOME_COLORS.ink : HOME_COLORS.muted,
      }}
    >
      {value}
    </Box>
  );
}

function LoadRow({
  row,
  subtitle,
  max,
  open,
  onToggle,
}: {
  row: HomeLoadRow;
  subtitle: string;
  max: number;
  open: boolean;
  onToggle(): void;
}) {
  const { stats } = row;
  return (
    <ButtonBase
      aria-expanded={open}
      onClick={onToggle}
      sx={{
        width: "100%",
        display: "grid",
        gridTemplateColumns: GRID,
        alignItems: "center",
        gap: 1,
        px: 0.5,
        py: 1.5,
        textAlign: "left",
        borderTop: `1px solid ${HOME_COLORS.line}`,
        "&:hover": { bgcolor: HOME_COLORS.soft },
      }}
    >
      <Box
        display="flex"
        sx={{
          transition: "transform .15s",
          transform: open ? "rotate(90deg)" : "none",
        }}
      >
        <MdChevronRight size={16} />
      </Box>
      <Box display="flex" flexDirection="column" minWidth={0}>
        <Typography sx={{ fontSize: 14, fontWeight: 500, ...ellipsis }}>
          {row.name}
        </Typography>
        <Typography
          sx={{ fontSize: 12, color: HOME_COLORS.muted, ...ellipsis }}
        >
          {subtitle}
        </Typography>
      </Box>
      <Box display="flex" flexDirection="column">
        <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
          {stats.ativos}
        </Typography>
        <Box
          sx={{
            height: 4,
            borderRadius: "2px",
            bgcolor: HOME_COLORS.line,
            mt: 0.5,
          }}
        >
          <Box
            sx={{
              height: "100%",
              borderRadius: "2px",
              bgcolor: HOME_COLORS.primary,
              width: `${Math.round((stats.ativos / max) * 100)}%`,
            }}
          />
        </Box>
      </Box>
      <StatCells stats={stats} />
    </ButtonBase>
  );
}

/** Próx. 7 dias, Aprov. parada, (Devolvidos) e Alertas, nas colunas da tabela. */
function StatCells({ stats }: { stats: HomeLoadRow["stats"] }) {
  return (
    <>
      <Count value={stats.proximos7} />
      <Count value={stats.aprovacaoParada} />
      {SHOW_RETURNED_COLUMN && <Count value={stats.devolvidos} />}
      {stats.semAprovacao > 0 ? (
        <Box
          data-alert
          sx={{
            justifySelf: "center",
            minWidth: 26,
            textAlign: "center",
            fontWeight: 700,
            fontSize: 13,
            px: 1,
            py: "2px",
            borderRadius: "10px",
            bgcolor: ALERT_TONE.bg,
            color: ALERT_TONE.color,
          }}
        >
          {stats.semAprovacao}
        </Box>
      ) : (
        <Count value={0} />
      )}
    </>
  );
}

const subItemSx = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) auto auto",
  alignItems: "center",
  gap: 1.5,
  px: 1.5,
  py: 1.1,
  borderRadius: "4px",
  bgcolor: HOME_COLORS.soft,
  textAlign: "left",
  "&:hover": { bgcolor: "#EDF0F4" },
} as const;

const linkSx = {
  alignSelf: "flex-start",
  textTransform: "none",
  px: 0,
  fontSize: 13,
} as const;

/** Até 5 projetos ativos do coordenador, por prioridade (busca ao expandir). */
function CoordinatorPreview({
  scope,
  today,
  coordinator,
  onOpenProject,
  onOpenList,
}: {
  scope: HomeScope;
  today?: string;
  coordinator: HomeLoadCoordinator;
  onOpenProject(projectId: string): void;
  onOpenList(): void;
}) {
  const { data, isLoading, isError } = useHomeCoordinatorProjects(
    scope,
    coordinator.id
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={1.5}>
        <CircularProgress size={20} />
      </Box>
    );
  }
  if (isError) {
    return (
      <Typography sx={{ fontSize: 13, color: HOME_COLORS.muted, py: 1 }}>
        Não foi possível carregar os projetos de {coordinator.name}.
      </Typography>
    );
  }

  return (
    <>
      {data?.items.map((project) => {
        const stuck =
          project.priority === "devolvido" ||
          project.priority === "aprovacaoParada";
        const detail =
          stuck && project.statusChangedAt && today
            ? `Há ${daysSince(project.statusChangedAt, today)} dias no status`
            : project.startDate
            ? `Início ${formatShortDay(saoPauloDayKey(project.startDate))}`
            : "Sem data de início";
        return (
          <ButtonBase
            key={project.id}
            onClick={() => onOpenProject(project.id)}
            sx={subItemSx}
          >
            <Box display="flex" flexDirection="column" minWidth={0}>
              <Typography sx={{ fontSize: 13, fontWeight: 500, ...ellipsis }}>
                {project.mmCode}/{project.yearCode} ·{" "}
                {project.eventName ?? "---"}
              </Typography>
              <Typography
                sx={{ fontSize: 12, color: HOME_COLORS.muted, ...ellipsis }}
              >
                {project.clientName ?? "---"}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 12, color: HOME_COLORS.muted }}>
              {detail}
            </Typography>
            {project.priority === "semAprovacao" ? (
              <NoApprovalChip />
            ) : (
              <StatusChip status={project.status} />
            )}
          </ButtonBase>
        );
      })}
      <Button onClick={onOpenList} sx={linkSx}>
        Ver os {coordinator.stats.ativos} projetos de {coordinator.name} na
        lista ›
      </Button>
    </>
  );
}

/**
 * Coordenadores da célula, com as mesmas colunas da linha da célula (quem
 * tem os eventos dos próximos 7 dias, as aprovações paradas e os alertas).
 */
function CellCoordinators({
  cell,
  onOpenCoordinator,
  onOpenCell,
}: {
  cell: HomeLoadRow;
  onOpenCoordinator(coordinator: HomeLoadCoordinator): void;
  onOpenCell(): void;
}) {
  const coordinators = cell.coordinators ?? [];
  return (
    <>
      {coordinators.length === 0 && (
        <Typography sx={{ fontSize: 13, color: HOME_COLORS.muted, py: 1 }}>
          Nenhum coordenador nesta célula.
        </Typography>
      )}
      {coordinators.map((coordinator) => (
        <ButtonBase
          key={coordinator.id}
          onClick={() => onOpenCoordinator(coordinator)}
          sx={{
            display: "grid",
            gridTemplateColumns: GRID,
            alignItems: "center",
            gap: 1,
            px: 0.5,
            py: 1.1,
            borderRadius: "4px",
            bgcolor: HOME_COLORS.soft,
            textAlign: "left",
            "&:hover": { bgcolor: "#EDF0F4" },
          }}
        >
          <span />
          <Box display="flex" flexDirection="column" minWidth={0}>
            <Typography sx={{ fontSize: 13, fontWeight: 500, ...ellipsis }}>
              {coordinator.name}
            </Typography>
            <Typography sx={{ fontSize: 12, color: HOME_COLORS.muted }}>
              Coord. Produção
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
            {coordinator.stats.ativos}
          </Typography>
          <StatCells stats={coordinator.stats} />
        </ButtonBase>
      ))}
      <Button onClick={onOpenCell} sx={{ ...linkSx, ml: 3.25 }}>
        Ver os {cell.stats.ativos} projetos da célula {cell.name} ›
      </Button>
    </>
  );
}
